// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createActionsHook,
	createStateHook,
	createContainer,
} from '@atlassian/react-sweet-state';
import { expVal } from '@atlassian/jira-feature-experiments';
import { BULK_EDIT_SELECTED_ISSUE_LIMIT } from '@atlassian/jira-bulk-operations-core/src/common/constants.tsx';
import { actions, initialState, type Actions } from './actions.tsx';
import type { SelectedIssuesState, IssueTableSelectedIssuesContainerProps } from './types.tsx';

export const IssueTableSelectedIssuesContainer =
	createContainer<IssueTableSelectedIssuesContainerProps>();

const Store = createStore<SelectedIssuesState, Actions, IssueTableSelectedIssuesContainerProps>({
	initialState,
	actions,
	name: 'jira-native-issue-table-selected-issues-state',
	containedBy: IssueTableSelectedIssuesContainer,
});

export const useIssueTableSelectedIssuesActions = createActionsHook<
	SelectedIssuesState,
	typeof actions
>(Store);

const getSelectedIssueIdsSelector = (state: SelectedIssuesState): string[] => {
	return Array.from(state.selectedIssueIds);
};

export const useIssueTableSelectedIssues = createStateHook(Store, {
	selector: getSelectedIssueIdsSelector,
});

const getSelectAllTotalSelected = (
	state: SelectedIssuesState,
	totalIssueSearchResultCount: number,
): number => {
	return (
		totalIssueSearchResultCount +
		Array.from(state.selectedIssueIds).length -
		Array.from(state.excludedIssueIds).length
	);
};

export const useIssueTableSelectAllTotalSelected = createStateHook(Store, {
	selector: getSelectAllTotalSelected,
});

const getIsIssueIdSelectedSelector = (
	state: SelectedIssuesState,
	{ rowIndex, issueId }: { rowIndex: number; issueId: string },
): boolean => {
	if (state.isSelectingAll && rowIndex > BULK_EDIT_SELECTED_ISSUE_LIMIT - 1) {
		return false;
	}

	return state.isSelectingAll || state.selectedIssueIds.has(issueId);
};

const getIsIssueIdSelectedSelectorOld = (state: SelectedIssuesState, issueId: string): boolean => {
	return state.selectedIssueIds.has(issueId);
};

// remove on clean up of bulk_ops_select_all_beyond_experiment
export const useIsIssueSelectedOld = createStateHook(Store, {
	selector: getIsIssueIdSelectedSelectorOld,
});

export const useIsIssueSelected = createStateHook(Store, {
	selector: getIsIssueIdSelectedSelector,
});

const getAreAllIssuesSelectedSelector = (
	state: SelectedIssuesState,
	issueIds?: string[],
): boolean => {
	if (expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) {
		return state.isSelectingAll;
	}
	if (!issueIds) return false;
	return issueIds.length > 0 && issueIds.every((issueId) => state.selectedIssueIds.has(issueId));
};

export const useAreAllIssuesSelected = createStateHook(Store, {
	selector: getAreAllIssuesSelectedSelector,
});

const getIsSelectingAll = (state: SelectedIssuesState): boolean => {
	return state.isSelectingAll;
};

export const useIsSelectingAll = createStateHook(Store, {
	selector: getIsSelectingAll,
});
